import { Link, useParams } from 'wouter';
import { HeaderFooterLayout } from '../app';
import { Partners } from '@platform/client';
import Divider from '../contentful/util/Divider';
import { Button } from '@platform/components';
import MicrosoftImpactPartnerDescription from './MicrosoftImpactPartnerDescription';
import MetaTags from '../util/MetaTags';
import ErrorPage from '../../ErrorPage';
import Skeleton from 'react-loading-skeleton';
import { MovingWorldsBannerLogo } from '../layout/MovingWorldsLogo';
import MicrosoftLogo from './microsoft-logo.png';

const widthClasses = 'mx-auto w-full max-w-7xl';

const ImpactPartnerPage = () => {
  const { impactPartnerName } = useParams<{ impactPartnerName: string }>();
  const { isSuccess, isError, data } = Partners.useGetImpactPartner(impactPartnerName);

  if (isError) {
    return <ErrorPage />;
  }

  if (!isSuccess) {
    return (
      <HeaderFooterLayout>
        <div className='flex flex-col gap-3 bg-white -mt-6 sm:-mt-12 px-4 sm:px-10 py-8'>
          <div className={`${widthClasses} flex flex-col sm:flex-row justify-between`}>
            <div className='max-w-[600px] flex flex-col gap-3'>
              <h1 className='my-3 font-header text-4xl sm:text-5xl leading-11 sm:leading-14 font-bold text-gray-500'>
                <Skeleton width={600} />
              </h1>
              <h2 className='mx-auto sm:mx-0 text-center sm:text-left font-header text-xl sm:text-2xl leading-6 sm:leading-7 text-gray-500'>
                <Skeleton width={300} />
              </h2>
              <div className='mt-8'>
                <div className='text-md text-gray-500 [&_p]:mb-3'>
                  <Skeleton width={700} />
                  <Skeleton width={700} />
                  <Skeleton width={700} />
                </div>
              </div>
            </div>
          </div>
          <div className='mt-40'>
            <div className='text-md text-gray-500 [&_p]:mb-3'>
              <Skeleton width={700} />
              <Skeleton width={700} />
              <Skeleton width={700} />
            </div>
          </div>
          {/*<div className='p-4 my-4 -mx-4 grid grid-cols-1 sm:grid-cols-3 gap-6'>
          <Skeleton height={300} />
          <Skeleton height={300} />
          <Skeleton height={300} />
        </div>*/}
        </div>
      </HeaderFooterLayout>
    );
  }

  return (
    <HeaderFooterLayout>
      <MetaTags
        title='Make a real impact with your professional skills benefitting our world'
        description='Find opportunities to make a real social impact with your professional skills to make a difference supporting our world'
      />
      <div className='flex flex-col gap-3 bg-white -mt-6 sm:-mt-12 px-4 sm:px-10 py-8'>
        <div className={`${widthClasses} flex flex-col sm:flex-row justify-between`}>
          <div className='max-w-[600px] flex flex-col gap-3'>
            <h1 className='my-3 font-header text-4xl sm:text-5xl leading-11 sm:leading-14 font-bold text-gray-500'>
              {data.impactPartner.name}
            </h1>
            <h2 className='mx-auto sm:mx-0 text-center sm:text-left font-header text-xl sm:text-2xl leading-6 sm:leading-7 text-gray-500'>
              A Program in Partnership with MovingWorlds
            </h2>
            <Divider className='mx-auto mt-0 sm:mx-0 !w-[260px]' />
            <div>
              {data.impactPartner.description && (
                <div
                  className='text-md text-gray-500 [&_p]:mb-3'
                  dangerouslySetInnerHTML={{
                    __html: data.impactPartner.description,
                  }}
                />
              )}
              <div className='w-fit my-4 sm:my-0 sm:pt-4'>
                <Button
                  size='lg'
                  variant='accent'
                  href={`/platform/auth/sign-up/representative?impact=${data.impactPartner.id}`}
                >
                  Create account
                </Button>
              </div>
            </div>
          </div>
          {data.impactPartner.logo && (
            <img className='mx-auto sm:mx-0 w-[200px] sm:-mt-20' src={data.impactPartner.logo} />
          )}
        </div>
        {impactPartnerName === 'microsoft' && (
          <>
            <div className={widthClasses}>
              <MicrosoftImpactPartnerDescription />
            </div>
            <div className='-mx-4 px-4 sm:-mx-10 sm:px-10 py-6 text-center flex flex-col justify-center bg-secondary-brand-25'>
              <div className='flex mx-auto h-[90px] items-center justify-center'>
                <img className='w-[138px]' src={MicrosoftLogo} />
                <div className='bg-gray-100 w-[2px] h-full mx-6' />
                <div className='w-[224px] h-[20px] text-brand-500'>
                  <MovingWorldsBannerLogo />
                </div>
              </div>
              <div className='text-2xl leading-14 font-open font-semibold text-gray-700'>
                Scaling AI for Good | Empowering Impact Enterprises Worldwide
              </div>
            </div>
          </>
        )}
        {data.orgs.length > 0 && (
          <div className='-mx-4 -my-8 px-4 pt-4 sm:-mx-10 sm:-my-10 sm:px-10 sm:mt-6 sm:pb-10 bg-gray-50'>
            <div className={widthClasses}>
              <div className='sm:py-6'>
                <h3 className='my-3 font-header text-base sm:text-xl leading-5 sm:leading-6 font-semibold text-gray-500'>
                  Explore impactful projects & enterprises
                </h3>
                <p>
                  See how organizations are benefiting from MovingWorlds and{' '}
                  {data.impactPartner.name} — and discover projects seeking support.
                </p>
              </div>
              <div className='p-4 my-4 -mx-4 grid grid-cols-1 sm:grid-cols-3 gap-6'>
                {data.orgs.map((org) => (
                  <Link
                    to={`~/platform/directory/organizations/${org.organizationId}`}
                    key={org.organizationId}
                    className='hover:cursor transition duration-200 ease-in-out hover:-translate-y-2 hover:shadow-xl'
                  >
                    <div className='h-full flex flex-col p-4 rounded-md bg-white shadow-lg gap-3'>
                      <img
                        src={org.logoUrl || undefined}
                        alt={`${org.name} logo`}
                        className='w-full h-32 sm:h-48 align-middle object-contain mb-2'
                      />
                      <h4 className='font-header text-base leading-5 font-semibold text-gray-500'>
                        {org.name}
                      </h4>
                      <p className='text-sm leading-5 text-gray-600'>{org.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </HeaderFooterLayout>
  );
};

export default ImpactPartnerPage;
